// LeaveRequestsList.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LeaveRequestForm from './LeaveRequestForm';
import EmployeeLeaveBalance from './EmployeeLeaveBalance';

const LeaveRequestsList = () => {
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showBalanceModal, setShowBalanceModal] = useState(false);
    const [selectedEmployeeForBalance, setSelectedEmployeeForBalance] = useState(null);
    const [filters, setFilters] = useState({
        status: 'All',
        search: '',
        start_date: '',
        end_date: ''
    });
    const navigate = useNavigate();

    const API_BASE = process.env.REACT_APP_API_BASE;

    const fetchLeaveRequests = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');

            // Build query string
            const params = new URLSearchParams();
            if (filters.status !== 'All') params.append('status', filters.status);
            if (filters.search) params.append('search', filters.search);
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);

            const url = `${API_BASE}leave/get_leave_requests.php?${params.toString()}`;

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setLeaveRequests(response.data.leave_requests);
                setError('');
            } else {
                setError(response.data.error || 'Failed to fetch leave requests');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to fetch leave requests');
            console.error('Error fetching leave requests:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE, filters]);

    useEffect(() => {
        fetchLeaveRequests();
    }, [fetchLeaveRequests]);

    const handleStatusUpdate = async (leaveId, newStatus, leaveRequest) => {
        let confirmMessage = '';

        if (newStatus === 'Approved') {
            confirmMessage = 'Are you sure you want to approve this leave request?';
        } else if (newStatus === 'Rejected') {
            confirmMessage = 'Are you sure you want to reject this leave request?';
        } else if (newStatus === 'Cancelled') {
            confirmMessage = 'Are you sure you want to cancel this leave request?';
        }

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(
                `${API_BASE}leave/update_leave_request_status.php`,
                { leave_id: leaveId, status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                let successMessage = `Leave request ${newStatus.toLowerCase()} successfully!`;

                // Show balance information if available
                if (response.data.data && response.data.data.balance_info) {
                    successMessage += `\nNew balance: ${response.data.data.balance_info.available_balance.toFixed(1)} days`;
                }

                alert(successMessage);
                // Auto-refresh the data
                fetchLeaveRequests();
            } else {
                // Show detailed error message if available
                if (response.data.details) {
                    alert(
                        `${response.data.error}\n\n` +
                        `Available Balance: ${response.data.details.available_balance.toFixed(1)} days\n` +
                        `Requested Days: ${response.data.details.requested_days.toFixed(1)} days\n` +
                        `Employee: ${response.data.details.employee}\n` +
                        `Leave Type: ${response.data.details.leave_type}`
                    );
                } else {
                    alert(response.data.error || 'Failed to update status');
                }
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Failed to update status';
            alert(errorMsg);
        } finally {
            // Always refresh the data to ensure UI is in sync with server state
            fetchLeaveRequests();
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'Pending': { class: 'bg-warning', icon: 'bi-clock' },
            'Approved': { class: 'bg-success', icon: 'bi-check-circle' },
            'Rejected': { class: 'bg-danger', icon: 'bi-x-circle' },
            'Cancelled': { class: 'bg-secondary', icon: 'bi-slash-circle' }
        };

        const config = statusConfig[status] || { class: 'bg-secondary', icon: 'bi-question-circle' };

        return (
            <span className={`badge ${config.class} d-flex align-items-center`}>
                <i className={`bi ${config.icon} me-1`}></i>
                {status}
            </span>
        );
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const resetFilters = () => {
        setFilters({
            status: 'All',
            search: '',
            start_date: '',
            end_date: ''
        });
    };

    const handleViewBalance = (empId, leaveTypeId, empName) => {
        setSelectedEmployeeForBalance(empId);
        setShowBalanceModal(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const calculateWorkDays = (fromDate, toDate, firstHalf, lastHalf) => {
        if (!fromDate || !toDate) return 0;

        const from = new Date(fromDate);
        const to = new Date(toDate);
        const timeDiff = to.getTime() - from.getTime();
        const dayDiff = Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;

        let totalDays = dayDiff;
        if (firstHalf) totalDays -= 0.5;
        if (lastHalf) totalDays -= 0.5;

        return totalDays > 0 ? totalDays : 0;
    };

    if (loading && leaveRequests.length === 0) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">Loading leave requests...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-8">
                                    <h2 className="h4 mb-1 fw-bold text-dark">
                                        <i className="bi bi-calendar-event me-2 text-primary"></i>
                                        Leave Requests Management
                                    </h2>
                                    <p className="text-muted mb-0">
                                        Manage, review, and approve employee leave requests
                                    </p>
                                </div>
                                <div className="col-md-4 text-end">
                                    <button
                                        className="btn btn-outline-secondary me-2"
                                        onClick={() => navigate('/leave-master')}
                                    >
                                        <i className="bi bi-arrow-left me-2"></i>
                                        Go Back
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => setShowAddModal(true)}
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        New Leave Request
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="form-label fw-medium">Status Filter</label>
                                    <select
                                        className="form-select"
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                    >
                                        <option value="All">All Status</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Rejected">Rejected</option>
                                        <option value="Cancelled">Cancelled</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-medium">From Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.start_date}
                                        onChange={(e) => handleFilterChange('start_date', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-medium">To Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.end_date}
                                        onChange={(e) => handleFilterChange('end_date', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-medium">Search</label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search by name, code, or leave type..."
                                            value={filters.search}
                                            onChange={(e) => handleFilterChange('search', e.target.value)}
                                        />
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={resetFilters}
                                            title="Reset all filters"
                                        >
                                            <i className="bi bi-arrow-clockwise"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Active Filters Indicator */}
                            {(filters.status !== 'All' || filters.search || filters.start_date || filters.end_date) && (
                                <div className="mt-3">
                                    <small className="text-muted">Active Filters:</small>
                                    <div className="d-flex flex-wrap gap-2 mt-1">
                                        {filters.status !== 'All' && (
                                            <span className="badge bg-primary">
                                                Status: {filters.status}
                                                <button
                                                    className="btn-close btn-close-white ms-1"
                                                    style={{ fontSize: '0.5rem' }}
                                                    onClick={() => handleFilterChange('status', 'All')}
                                                ></button>
                                            </span>
                                        )}
                                        {filters.start_date && (
                                            <span className="badge bg-info">
                                                From: {formatDate(filters.start_date)}
                                                <button
                                                    className="btn-close btn-close-white ms-1"
                                                    style={{ fontSize: '0.5rem' }}
                                                    onClick={() => handleFilterChange('start_date', '')}
                                                ></button>
                                            </span>
                                        )}
                                        {filters.end_date && (
                                            <span className="badge bg-info">
                                                To: {formatDate(filters.end_date)}
                                                <button
                                                    className="btn-close btn-close-white ms-1"
                                                    style={{ fontSize: '0.5rem' }}
                                                    onClick={() => handleFilterChange('end_date', '')}
                                                ></button>
                                            </span>
                                        )}
                                        {filters.search && (
                                            <span className="badge bg-warning">
                                                Search: {filters.search}
                                                <button
                                                    className="btn-close btn-close-white ms-1"
                                                    style={{ fontSize: '0.5rem' }}
                                                    onClick={() => handleFilterChange('search', '')}
                                                ></button>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div className="flex-grow-1">{error}</div>
                            <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={fetchLeaveRequests}
                            >
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Stats */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-3">
                                    <div className="fw-bold text-primary fs-4">{leaveRequests.length}</div>
                                    <small className="text-muted">Total Requests</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-warning fs-4">
                                        {leaveRequests.filter(r => r.status === 'Pending').length}
                                    </div>
                                    <small className="text-muted">Pending</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-success fs-4">
                                        {leaveRequests.filter(r => r.status === 'Approved').length}
                                    </div>
                                    <small className="text-muted">Approved</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-danger fs-4">
                                        {leaveRequests.filter(r => r.status === 'Rejected').length}
                                    </div>
                                    <small className="text-muted">Rejected</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Leave Requests Table */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Employee</th>
                                            <th>Leave Type</th>
                                            <th>Date Range</th>
                                            <th>Duration</th>
                                            <th>Reason</th>
                                            <th>Status</th>
                                            <th>Submitted</th>
                                            <th className="text-end pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leaveRequests.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5">
                                                    <i className="bi bi-calendar-x display-4 text-muted"></i>
                                                    <p className="mt-3 text-muted">No leave requests found</p>
                                                    {Object.values(filters).some(v => v) && (
                                                        <button
                                                            className="btn btn-outline-primary mt-2"
                                                            onClick={resetFilters}
                                                        >
                                                            Clear filters to see all requests
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ) : (
                                            leaveRequests.map(request => {
                                                const workDays = calculateWorkDays(
                                                    request.from_date,
                                                    request.to_date,
                                                    request.first_day_half,
                                                    request.last_day_half
                                                );

                                                return (
                                                    <tr key={request.leave_id} className={request.status === 'Pending' ? 'table-warning' : ''}>
                                                        <td className="ps-4">
                                                            <div className="d-flex align-items-center">
                                                                <div className="employee-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                                                    style={{ width: '40px', height: '40px' }}
                                                                >
                                                                    <i className="bi bi-person"></i>
                                                                </div>
                                                                <div>
                                                                    <div className="fw-medium">{request.first_name} {request.last_name}</div>
                                                                    <small className="text-muted d-block">
                                                                        {request.emp_code || 'N/A'}
                                                                    </small>
                                                                    <small className="text-muted">
                                                                        {request.email || 'No email'}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="badge bg-info me-2">
                                                                    {request.leave_code || 'N/A'}
                                                                </div>
                                                                <div>
                                                                    {request.leave_name || 'Unknown Type'}
                                                                    {request.is_paid === 0 && (
                                                                        <small className="text-danger d-block">Unpaid Leave</small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="fw-medium">{formatDate(request.from_date)}</div>
                                                            <div className="text-muted small">to</div>
                                                            <div className="fw-medium">{formatDate(request.to_date)}</div>
                                                            {(request.first_day_half || request.last_day_half) && (
                                                                <div className="mt-1">
                                                                    {request.first_day_half && (
                                                                        <span className="badge bg-primary me-1">
                                                                            First: {request.first_day_period || 'Half'}
                                                                        </span>
                                                                    )}
                                                                    {request.last_day_half && (
                                                                        <span className="badge bg-info">
                                                                            Last: {request.last_day_period || 'Half'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="fw-bold text-primary">
                                                                {parseFloat(request.total_days || workDays).toFixed(1)} days
                                                            </div>
                                                            <small className="text-muted">
                                                                {request.first_day_half && request.last_day_half ? 'Both half days' :
                                                                    request.first_day_half ? 'First half day' :
                                                                        request.last_day_half ? 'Last half day' : 'Full days'}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <div
                                                                className="text-truncate"
                                                                style={{ maxWidth: '200px' }}
                                                                title={request.reason}
                                                            >
                                                                {request.reason || 'No reason provided'}
                                                            </div>
                                                            {request.reason && request.reason.length > 50 && (
                                                                <button
                                                                    className="btn btn-link btn-sm p-0 mt-1"
                                                                    onClick={() => alert(`Reason:\n\n${request.reason}`)}
                                                                >
                                                                    View Full Reason
                                                                </button>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {getStatusBadge(request.status)}
                                                            {request.approved_by_name && (
                                                                <div className="small text-muted mt-1">
                                                                    By: {request.approved_by_name}
                                                                </div>
                                                            )}
                                                            {request.approved_at && (
                                                                <div className="small text-muted">
                                                                    {formatDate(request.approved_at)}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div>{formatDate(request.created_at)}</div>
                                                            <small className="text-muted">
                                                                {new Date(request.created_at).toLocaleTimeString([], {
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })}
                                                            </small>
                                                        </td>
                                                        <td className="text-end pe-4">
                                                            <div className="btn-group btn-group-sm" role="group">
                                                                {request.status === 'Pending' && (
                                                                    <>
                                                                        <button
                                                                            className="btn btn-outline-success"
                                                                            onClick={() => handleStatusUpdate(request.leave_id, 'Approved', request)}
                                                                            title="Approve Leave"
                                                                            data-testid={`approve-${request.leave_id}`}
                                                                        >
                                                                            <i className="bi bi-check"></i>
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-outline-danger"
                                                                            onClick={() => handleStatusUpdate(request.leave_id, 'Rejected', request)}
                                                                            title="Reject Leave"
                                                                        >
                                                                            <i className="bi bi-x"></i>
                                                                        </button>
                                                                    </>
                                                                )}

                                                                {/* Balance Check Button - Always Visible */}
                                                                <button
                                                                    className="btn btn-outline-info"
                                                                    onClick={() => handleViewBalance(
                                                                        request.emp_id,
                                                                        request.leave_type_id,
                                                                        `${request.first_name} ${request.last_name}`
                                                                    )}
                                                                    title="Check Leave Balance"
                                                                >
                                                                    <i className="bi bi-calculator"></i>
                                                                </button>

                                                                {/* Cancel Button only for Pending requests */}
                                                                {request.status === 'Pending' && (
                                                                    <button
                                                                        className="btn btn-outline-secondary"
                                                                        onClick={() => handleStatusUpdate(request.leave_id, 'Cancelled', request)}
                                                                        title="Cancel Leave"
                                                                    >
                                                                        <i className="bi bi-slash-circle"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination or Summary */}
                            {leaveRequests.length > 0 && (
                                <div className="card-footer bg-light">
                                    <div className="row align-items-center">
                                        <div className="col-md-6">
                                            <small className="text-muted">
                                                Showing {leaveRequests.length} leave request{leaveRequests.length !== 1 ? 's' : ''}
                                            </small>
                                        </div>
                                        <div className="col-md-6 text-end">
                                            <button
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={fetchLeaveRequests}
                                                title="Refresh data"
                                            >
                                                <i className="bi bi-arrow-clockwise me-1"></i>
                                                Refresh
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions Footer */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card border-0">
                        <div className="card-body text-center py-3">
                            <div className="btn-group" role="group">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={fetchLeaveRequests}
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    Refresh Data
                                </button>
                                <button
                                    className="btn btn-outline-success"
                                    onClick={() => setShowAddModal(true)}
                                >
                                    <i className="bi bi-plus-circle me-2"></i>
                                    New Leave Request
                                </button>
                                <button
                                    className="btn btn-outline-info"
                                    onClick={() => {
                                        // Open balance modal for all employees
                                        setSelectedEmployeeForBalance(null);
                                        setShowBalanceModal(true);
                                    }}
                                >
                                    <i className="bi bi-calculator me-2"></i>
                                    View All Balances
                                </button>
                                <button
                                    className="btn btn-outline-warning"
                                    onClick={resetFilters}
                                >
                                    <i className="bi bi-filter me-2"></i>
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {showAddModal && (
                <LeaveRequestForm
                    show={showAddModal}
                    onHide={() => setShowAddModal(false)}
                    onLeaveRequestAdded={fetchLeaveRequests}
                />
            )}

            {showBalanceModal && (
                <EmployeeLeaveBalance
                    employeeId={selectedEmployeeForBalance}
                    show={showBalanceModal}
                    onHide={() => {
                        setShowBalanceModal(false);
                        setSelectedEmployeeForBalance(null);
                    }}
                />
            )}
        </div>
    );
};

export default LeaveRequestsList;